import { Component, OnInit } from '@angular/core';
import { InceptionService, TranslationService } from '@proximus/ng-common';
import * as PublicWifiPxsplusActions from '../store/actions/public-wifi-pxsplus.actions';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import {
  ApplicationState,
  getPublicWifiPxsplusConfigureURL,
  getPublicWifiPxsplusEligibleProducts,
  getPublicWifiPxsplusProductsJwt,
  getPublicWifiPxsplusProductsPending,
} from '../../store/reducers';
import { IPublicWifiProduct } from '../store/reducers/public-wifi-pxsplus.reducer';
import { OnDestroyHookDirective } from 'src/app/shared/hooks';
import { getPiiServiceId } from 'src/app/shared/pii/pii.utils';
import { overrideSessionStorageOnEmptyHistory } from 'src/app/shared/usage/util';
import { deduceDeviceType, getPublicWiFiPxsplusProductServiceId } from './public-wifi-pxsplus.utils';

enum ActiveScreen {
  LANDPAGE = 'landpage',
  CONFIGURATION = 'configuration',
}

@Component({
  selector: 'mprd-public-wifi-pxsplus',
  templateUrl: './public-wifi-pxsplus.component.html',
  styleUrls: ['./public-wifi-pxsplus.component.css'],
})
export class PublicWifiPxsplusComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  backUrlFromApp: string;
  deviceType: string;
  hasUniqueProduct: boolean;
  hasConfiguredProduct: boolean;
  accessNumber: string | number;
  activeScreen: ActiveScreen = ActiveScreen.LANDPAGE;
  configureUrl: string;
  jwt: string;
  showBackButton = false;

  private dataLoaded?: boolean;

  constructor(
    private store: Store<ApplicationState>,
    private inceptionService: InceptionService,
    private translationService: TranslationService,
    private router: Router,
  ) {
    super();
  }

  ngOnInit() {
    this.deviceType = deduceDeviceType();
    this.accessNumber = getPiiServiceId();

    if (this.deviceType === 'android') {
      return this.navigateToStaticPage();
    }

    this.store.dispatch(
      new PublicWifiPxsplusActions.GetPublicWifiPxsplusProducts(),
    );

    this.addSubscription(
      combineLatest([
        this.store.select(getPublicWifiPxsplusEligibleProducts),
        this.store.select(getPublicWifiPxsplusProductsJwt),
        this.store.select(getPublicWifiPxsplusProductsPending),
      ]).subscribe((res: [Array<IPublicWifiProduct>, string, boolean]) => {
        const products = res[0];
        const jwt = res[1];
        const isLoading = res[2];
        if (jwt) {
          this.jwt = jwt;
          this.store.dispatch(
            new PublicWifiPxsplusActions.GetPublicWifiPxsplusConfigureURL({
              serviceId: getPublicWiFiPxsplusProductServiceId(products),
              jwt: this.jwt,
            }),
          );
        }
        if (products && products.length > 0 && !this.dataLoaded) {
          this.dataLoaded = true;
          if (
            !products.some(
              (product) => product.serviceId.toString() === this.accessNumber,
            )
          ) {
            return this.navigateToOverview();
          }
          this.showBackButton = true;
          if (products.length === 1) {
            this.hasUniqueProduct = true;
            if (products[0]?.configured) {
              this.hasConfiguredProduct = true;
            }
          } else {
            this.hasUniqueProduct = false;
            this.hasConfiguredProduct = products.some(
              (productPublicWifi: IPublicWifiProduct) =>
                productPublicWifi?.configured === true,
            );
          }
          if (this.hasConfiguredProduct) {
            this.activeScreen = ActiveScreen.CONFIGURATION;
          }
        } else {
          // Data must be loaded at least one time to know if we must redirect or not
          if (!isLoading && products.length === 0) {
            this.navigateToOverview();
          }
          this.showBackButton = true;
        }
      }),
    );

    this.addSubscription(
      this.store
        .select(getPublicWifiPxsplusConfigureURL)
        .subscribe((configureUrl) => {
          if (configureUrl) {
            this.configureUrl = configureUrl;
          }
        }),
    );

    if (window['MyProximusMobileApp']) {
      this.backUrlFromApp = this.translationService.getTranslatedValueFor(
        'common.myproximus.back.url',
      );
    }
    setTimeout(() => {
      this.inceptionService.init();
    });
  }

  navigateToOverview = () => {
    overrideSessionStorageOnEmptyHistory(
      window.location.href?.replace('pxsplus/public-wifi', 'overview'),
    );
    this.router.navigate(['overview']);
  };

  navigateToStaticPage = () => {
    overrideSessionStorageOnEmptyHistory(
      window.location.href + '/howtoinstall',
    );
    this.router.navigate(['pxsplus/public-wifi/howtoinstall']);
  };

  goBack() {
    this.backUrlFromApp
      ? window.open(this.backUrlFromApp)
      : window.history.back();
  }
}
